import { useEffect, useMemo, useState } from "react";
import { motion } from "motion/react";
import { Icon } from "@iconify-icon/react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { getCookieConsent, setCookieConsent } from "@/utils/cookies";

export default function CookieConsent() {
  const { t } = useTranslation(["common"]);
  const [visible, setVisible] = useState(false);
  const [open, setOpen] = useState(false);
  const [analytics, setAnalytics] = useState(true);

  useEffect(() => {
    const existing = getCookieConsent();

    if (!existing) {
      setVisible(true);
    }
  }, []);

  const onAcceptAll = () => {
    setCookieConsent({ necessary: true, analytics: true, timestamp: Date.now() });
    setVisible(false);
  };

  const onSave = () => {
    setCookieConsent({ necessary: true, analytics, timestamp: Date.now() });
    setVisible(false);
    setOpen(false);
  };

  const decorative = useMemo(
    () => (
      <>
        <div className="absolute inset-0 overflow-hidden rounded-2xl">
          <div className="absolute -top-10 -left-10 h-24 w-24 rounded-full bg-gradient-to-br from-blue-400 to-purple-600 opacity-5" />
          <div className="absolute -right-8 -bottom-8 h-20 w-20 rounded-full bg-gradient-to-tr from-green-400 to-teal-500 opacity-5" />
        </div>
      </>
    ),
    [],
  );

  if (!visible) return null;

  return (
    <motion.div
      animate={{ opacity: 1, y: 0 }}
      className={
        "fixed bottom-0 left-1/2 z-[10000] w-full max-w-6xl -translate-x-1/2 " +
        (open ? " hidden" : "")
      }
      initial={{ opacity: 0, y: 20 }}
      transition={{ duration: 0.4 }}
    >
      <div className="w-full p-3 sm:p-6">
        <div className="bg-side-accent/80 relative rounded-2xl shadow-2xl backdrop-blur-xl">
          {decorative}
          <div className="relative flex flex-col gap-4 p-4 sm:p-6">
            {/* Mobile header: icon + title inline, description below */}
            <div className="sm:hidden">
              <div className="flex items-center gap-2">
                <div className="bg-background/50 flex items-center justify-center rounded-lg p-1">
                  <Icon height="1.25rem" icon="mdi:cookie" width="1.25rem" />
                </div>
                <div className="text-accent-foreground text-base font-semibold">
                  {t("common:cookie.title", "We use cookies")}
                </div>
              </div>
              <p className="text-muted-foreground mt-2 text-sm">
                {t(
                  "common:cookie.description",
                  "We use necessary cookies to make our site work and analytics cookies to understand how you use it. You can accept all, reject analytics, or manage your preferences.",
                )}
              </p>
            </div>

            {/* Desktop/tablet header: icon aside, text block with scroll */}
            <div className="hidden items-start gap-3 sm:flex">
              <div className="bg-background/50 flex items-center justify-center rounded-lg p-2">
                <Icon height="1.5rem" icon="mdi:cookie" width="1.5rem" />
              </div>
              <div className="flex-1">
                <div className="text-accent-foreground text-lg font-semibold">
                  {t("common:cookie.title", "We use cookies")}
                </div>
                <ScrollArea className="max-h-36 pr-4">
                  <p className="text-muted-foreground text-sm">
                    {t(
                      "common:cookie.description",
                      "We use necessary cookies to make our site work and analytics cookies to understand how you use it. You can accept all, reject analytics, or manage your preferences.",
                    )}
                  </p>
                </ScrollArea>
              </div>
            </div>

            {/* Buttons: mobile layout */}
            <div className="sm:hidden">
              <div className="w-full">
                <Button
                  className={"w-full text-xs"}
                  size="sm"
                  variant="foreground"
                  onClick={onAcceptAll}
                >
                  {t("common:cookie.acceptAll", "Accept all")}
                </Button>
              </div>
              <div className="mt-2">
                <Button
                  className="w-full text-xs"
                  size="sm"
                  variant="foreground"
                  onClick={() => setOpen(true)}
                >
                  {t("common:cookie.manage", "Manage preferences")}
                </Button>
              </div>
            </div>

            {/* Buttons: desktop/tablet layout */}
            <div className="hidden sm:flex sm:flex-row sm:items-center sm:justify-between">
              <div className="flex w-full flex-wrap items-center justify-end gap-2">
                <Button variant="foreground" onClick={() => setOpen(true)}>
                  {t("common:cookie.manage", "Manage preferences")}
                </Button>
                <Dialog open={open} onOpenChange={setOpen}>
                  <DialogContent className="bg-side-accent/80 max-w-3xl rounded-2xl shadow-2xl backdrop-blur-xl">
                    <div className="absolute inset-0 overflow-hidden rounded-2xl">
                      <div className="absolute -top-10 -left-10 h-24 w-24 rounded-full bg-gradient-to-br from-blue-400 to-purple-600 opacity-5" />
                      <div className="absolute -right-8 -bottom-8 h-20 w-20 rounded-full bg-gradient-to-tr from-green-400 to-teal-500 opacity-5" />
                    </div>
                    <div className="relative">
                      <DialogHeader>
                        <DialogTitle className="text-accent-foreground">
                          {t("common:cookie.dialog.title", "Cookie preferences")}
                        </DialogTitle>
                        <DialogDescription>
                          {t(
                            "common:cookie.dialog.description",
                            "Adjust how we use cookies. Necessary cookies are always on to ensure core functionality (security, authentication, language). Analytics cookies help us improve the product by collecting usage information.",
                          )}
                        </DialogDescription>
                      </DialogHeader>
                      <div className="mt-4 space-y-4">
                        <div className="bg-background/50 rounded-xl p-4">
                          <div className="flex items-center justify-between gap-4">
                            <div>
                              <div className="text-accent-foreground font-medium">
                                {t("common:cookie.dialog.analyticsTitle", "Analytics cookies")}
                              </div>
                              <div className="text-muted-foreground text-sm">
                                {t(
                                  "common:cookie.dialog.analyticsDesc",
                                  "Helps us understand feature usage, performance, and errors. We use this to prioritize improvements and fix issues. No marketing cookies are used.",
                                )}
                              </div>
                            </div>
                            <Switch
                              checked={analytics}
                              className="bg-accent-foreground! scale-75 data-[state=checked]:[&_>_span]:bg-emerald-500! data-[state=unchecked]:[&_>_span]:bg-red-600!"
                              onCheckedChange={setAnalytics}
                            />
                          </div>
                        </div>

                        <div className="bg-background/50 rounded-xl p-4">
                          <div className="text-accent-foreground font-medium">
                            {t("common:cookie.dialog.necessaryTitle", "Necessary cookies")}
                          </div>
                          <div className="text-muted-foreground text-sm">
                            {t(
                              "common:cookie.dialog.necessaryDesc",
                              "Required for things like security, session management, preferred language, and accessibility. These cannot be disabled.",
                            )}
                          </div>
                        </div>

                        <div className="flex items-center justify-end gap-2 pt-2">
                          <Button variant="foreground" onClick={onSave}>
                            {t("common:cookie.save", "Save preferences")}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
                <Button variant="foreground" onClick={onAcceptAll}>
                  {t("common:cookie.acceptAll", "Accept all")}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
